import React from "react";
import { Box, Typography } from "@mui/material";
import { usePortal } from "../../authContext/portalContext";
import { consumeJustLoggedIn } from "../../Services/sessionStorage";
import { useTranslation } from "../../i18n/LanguageContext";

const PORTAL_ICONS = {
  admin: "🛠️",
  therapist: "🩺",
  patient: "🧑",
};

const HOLD_MS = 2800;
const ANIMATION_MS = 300;

// "Dynamic island" style pill — top-center, slides/fades in, holds briefly,
// slides/fades out on its own. Mounted once (in MainLayout) so it's present
// on every authenticated page. Two triggers: right after a fresh login
// (authContext's applySession marks it, consumed once here) and every
// explicit portal switch afterwards (Sidebar's bottom portal list already
// calls usePortal().switchPortal()).
function RoleSwitchIndicator() {
  const { t } = useTranslation();
  const { activePortal } = usePortal();
  const [visible, setVisible] = React.useState(false);
  const [entering, setEntering] = React.useState(false);
  const [portalToShow, setPortalToShow] = React.useState(null);
  const prevPortalRef = React.useRef(activePortal);
  const hideTimeoutRef = React.useRef(null);
  const unmountTimeoutRef = React.useRef(null);

  const triggerShow = React.useCallback((portal) => {
    if (!portal) return;
    clearTimeout(hideTimeoutRef.current);
    clearTimeout(unmountTimeoutRef.current);
    setPortalToShow(portal);
    setVisible(true);
    setEntering(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setEntering(true)));
    hideTimeoutRef.current = setTimeout(() => {
      setEntering(false);
      unmountTimeoutRef.current = setTimeout(() => setVisible(false), ANIMATION_MS);
    }, HOLD_MS);
  }, []);

  React.useEffect(() => {
    if (consumeJustLoggedIn()) triggerShow(activePortal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (prevPortalRef.current !== activePortal) {
      prevPortalRef.current = activePortal;
      triggerShow(activePortal);
    }
  }, [activePortal, triggerShow]);

  React.useEffect(
    () => () => {
      clearTimeout(hideTimeoutRef.current);
      clearTimeout(unmountTimeoutRef.current);
    },
    []
  );

  if (!visible || !portalToShow) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: { xs: 72, md: 16 },
        left: "50%",
        transform: entering ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(-16px)",
        opacity: entering ? 1 : 0,
        transition: `transform ${ANIMATION_MS}ms ease, opacity ${ANIMATION_MS}ms ease`,
        zIndex: 9999,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: "#163666",
        color: "#fff",
        borderRadius: "999px",
        px: 2,
        py: 1,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      }}
    >
      <Box component="span" aria-hidden sx={{ fontSize: "0.9375rem", lineHeight: 1 }}>
        {PORTAL_ICONS[portalToShow] || "👤"}
      </Box>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, whiteSpace: "nowrap" }}>
        {t("portal.roleSwitchIndicator.viewingAs", {
          role: t(`sidebar.roleLabels.${portalToShow}`) || portalToShow,
        })}
      </Typography>
    </Box>
  );
}

export default RoleSwitchIndicator;
